import { zoneNamesMatch } from '@utils/sceneObjects/applyCatalogSwatchUvs'
import { isLateralOrCostadoLinkedCatalog } from '@utils/sceneObjects/linked/linkedTextureFields'
import { StoredTextureLike } from '@utils/sceneObjects/linked/textureCatalogApi'
import { isUserAppliedLinkedTexture } from '@utils/sceneObjects/linked/reconcileTexturesAfterUnitChange'

type ZoneEntry = {
  name?: string
  material?: {
    zoneDisplayName?: string
    zone?: string
    linkedTextureID?: number
  }
  idAcabadoUnidadModeloAcabado?: string
}

const KNOWN_DISPLAY_ZONE_NAMES = new Set([
  'Puerta',
  'Zocalo',
  'Zócalo',
  'Armazon',
  'Armazón',
  'Patas',
  'Gola',
  'Costado',
  'Encimera',
  'Frente',
])

/** Acabado de mueble: nunca offset/rotación aleatoria (también kits sin free-dim). */
const STABLE_FURNITURE_FINISH_ZONES = new Set([
  'Puerta',
  'Costado',
  'Frente',
  'Armazon',
  'Armazón',
  'Zocalo',
  'Zócalo',
  'Regleta',
])

/** Paneles finos: UV por los dos ejes grandes, no por la normal del canto. */
const PANEL_FINISH_ZONES = new Set(['Puerta', 'Costado', 'Frente'])

const DOOR_FINISH_ZONES = new Set(['Puerta', 'Frente'])

const NON_DOOR_APPLIANCE_ZONES = new Set([
  'Encimera',
  'Zocalo',
  'Zócalo',
  'Armazon',
  'Armazón',
  'Gola',
  'Patas',
  'Costado',
  'Regleta',
  'Luz',
  'Tirador',
  'Inox',
  'Logo',
  'Numeros',
])

export function getZoneLogicalName(
  entry?: { zoneDisplayName?: string; zone?: string; name?: string } | null,
): string | undefined {
  const candidates = [entry?.zoneDisplayName, entry?.zone, entry?.name]
  for (const candidate of candidates) {
    if (candidate && KNOWN_DISPLAY_ZONE_NAMES.has(candidate)) return candidate
  }
  return entry?.zoneDisplayName || entry?.zone || undefined
}

export function isLinkedFurnitureFinishZone(name?: string | null): boolean {
  return Boolean(name && STABLE_FURNITURE_FINISH_ZONES.has(name))
}

export function isLinkedFurnitureFinishTexture(texture?: {
  zone?: string
  zoneDisplayName?: string
  modelo?: string
} | null): boolean {
  if (
    isLinkedFurnitureFinishZone(texture?.zoneDisplayName) ||
    isLinkedFurnitureFinishZone(texture?.zone)
  ) {
    return true
  }
  return texture?.modelo === 'AcabadoModelo'
}

export function isLinkedPanelFinishZone(name?: string | null): boolean {
  return Boolean(name && PANEL_FINISH_ZONES.has(name))
}

export function isLinkedPanelFinishTexture(texture?: {
  zone?: string
  zoneDisplayName?: string
  modelo?: string
} | null): boolean {
  if (
    isLinkedPanelFinishZone(texture?.zoneDisplayName) ||
    isLinkedPanelFinishZone(texture?.zone)
  ) {
    return true
  }
  if (texture?.modelo !== 'AcabadoModelo') return false
  const logical = getZoneLogicalName(texture)
  if (!logical) return true
  if (PANEL_FINISH_ZONES.has(logical)) return true
  if (NON_DOOR_APPLIANCE_ZONES.has(logical) || logical === 'Encimera') return false
  return DOOR_FINISH_ZONES.has(logical)
}

export function isDoorFinishTexture(texture?: {
  zone?: string
  zoneDisplayName?: string
} | null): boolean {
  const logical = getZoneLogicalName(texture)
  return Boolean(logical && DOOR_FINISH_ZONES.has(logical))
}

/**
 * Contrato único de acabado: la zona del editor y la de la textura tienen que ser
 * la misma. Sin alias (Puerta ≠ frente CAD de lavavajillas).
 */
export function textureBelongsToZone(
  texture?: Pick<StoredTextureLike, 'zoneDisplayName'> | null,
  zoneDisplayName?: string | null,
): boolean {
  return Boolean(zoneDisplayName) && texture?.zoneDisplayName === zoneDisplayName
}

export function textureReceivesDoorFinish(
  _item:
    | {
        catalogID?: string | number
        codigo?: string | number
        referencia?: string
        description?: string | null
        typeOfSon?: string | null
      }
    | null
    | undefined,
  texture?: StoredTextureLike | null,
): boolean {
  return isDoorFinishTexture(texture)
}

export function findDoorFinishTexture(
  item:
    | {
        catalogID?: string | number
        codigo?: string | number
        referencia?: string
        description?: string | null
        typeOfSon?: string | null
        textures?: StoredTextureLike[]
      }
    | null
    | undefined,
): StoredTextureLike | undefined {
  const textures = item?.textures
  if (!textures?.length) return undefined
  return textures.find((t) => isDoorFinishTexture(t))
}

function doorLogicalNamesMatch(a?: string, b?: string): boolean {
  if (!a || !b) return false
  if (a === b) return true
  return DOOR_FINISH_ZONES.has(a) && DOOR_FINISH_ZONES.has(b)
}

/** Nombre lógico de zona (Puerta, Zócalo…) frente a clave de mesh (Mesh001_2 / Cube002_1). */
export function isMeshZoneKey(name: string): boolean {
  return /^(Mesh|Cube)\d+/i.test(name.trim())
}

function countZonesWithDisplayName(
  zones: Record<string, ZoneEntry>,
  displayName: string,
): number {
  let count = 0
  for (const [key, zone] of Object.entries(zones)) {
    const mat = zone?.material
    if (!mat) continue
    if (
      mat.zoneDisplayName === displayName ||
      mat.zone === displayName ||
      key === displayName ||
      zone.name === displayName
    ) {
      count++
    }
  }
  return count
}

function findUniqueZoneKeyByDisplayName(
  zones: Record<string, ZoneEntry>,
  displayName: string,
): string | null {
  if (countZonesWithDisplayName(zones, displayName) !== 1) return null

  for (const [key, zone] of Object.entries(zones)) {
    const mat = zone?.material
    if (!mat) continue
    if (
      mat.zoneDisplayName === displayName ||
      mat.zone === displayName ||
      key === displayName ||
      zone.name === displayName
    ) {
      return key
    }
  }
  return null
}

function findZoneKeyByExactAcabadoId(
  zones: Record<string, ZoneEntry>,
  compositeId: string | number,
): string | null {
  const id = String(compositeId)
  for (const [key, zone] of Object.entries(zones)) {
    const zoneComposite = zone?.idAcabadoUnidadModeloAcabado
    if (zoneComposite && String(zoneComposite) === id) return key
  }
  return null
}

/**
 * Resuelve la clave de `zones` para una entrada de `textures[]`.
 * Prioridad: zoneName mesh exacto → zone.name → displayName único → idAcabado exacto.
 */
export function findZoneKeyForTexture(
  zones: Record<string, ZoneEntry>,
  texture: StoredTextureLike,
): string | null {
  if (!zones || !texture) return null

  const zoneName = texture.zoneName?.trim()
  if (zoneName) {
    if (zones[zoneName]) return zoneName

    for (const [key, zone] of Object.entries(zones)) {
      if (zone?.name === zoneName || key === zoneName) {
        return key
      }
    }

    if (isMeshZoneKey(zoneName) || KNOWN_DISPLAY_ZONE_NAMES.has(zoneName)) {
      const byLogicalName = findUniqueZoneKeyByDisplayName(zones, zoneName)
      if (byLogicalName) return byLogicalName
    } else if (!isMeshZoneKey(zoneName)) {
      const byLogicalName = findUniqueZoneKeyByDisplayName(zones, zoneName)
      if (byLogicalName) return byLogicalName
    }
  }

  const displayName = texture.zoneDisplayName || texture.zone
  if (displayName) {
    const byDisplay = findUniqueZoneKeyByDisplayName(zones, displayName)
    if (byDisplay) return byDisplay
  }

  const compositeId = texture.idAcabadoUnidadModeloAcabado
  if (compositeId) {
    return findZoneKeyByExactAcabadoId(zones, compositeId)
  }

  return null
}

/** Ordena texturas: claves mesh exactas primero, luego nombres más específicos. */
export function sortTexturesForZoneSync<T extends StoredTextureLike>(
  textures: T[],
  zones?: Record<string, ZoneEntry>,
): T[] {
  return [...textures].sort((a, b) => {
    const aExact = zones && a.zoneName && zones[a.zoneName] ? 1 : 0
    const bExact = zones && b.zoneName && zones[b.zoneName] ? 1 : 0
    if (aExact !== bExact) return bExact - aExact

    const aMesh = a.zoneName && isMeshZoneKey(a.zoneName) ? 1 : 0
    const bMesh = b.zoneName && isMeshZoneKey(b.zoneName) ? 1 : 0
    if (aMesh !== bMesh) return bMesh - aMesh

    const lenA = a.zoneName?.length ?? 0
    const lenB = b.zoneName?.length ?? 0
    return lenB - lenA
  })
}

/**
 * Tras cambio de GLB, alinea zoneName/linkedTextureID de textures con las zonas reales del mesh.
 */
export function alignTextureZoneNamesWithZones(
  textures: StoredTextureLike[],
  zones: Record<string, ZoneEntry>,
): StoredTextureLike[] {
  return textures.map((texture) => {
    const zoneKey = findZoneKeyForTexture(zones, texture)
    if (!zoneKey) return texture

    const zone = zones[zoneKey]
    const mat = zone?.material
    const linkedFromZone = mat?.linkedTextureID

    return {
      ...texture,
      zoneName: zoneKey,
      zoneDisplayName: mat?.zoneDisplayName ?? texture.zoneDisplayName,
      zone: mat?.zone ?? mat?.zoneDisplayName ?? texture.zone,
      linkedTextureID:
        typeof linkedFromZone === 'number' && linkedFromZone > 0
          ? linkedFromZone
          : texture.linkedTextureID,
    }
  })
}

/** Resuelve la clave mesh en `zones` para aplicar o sincronizar una textura. */
export function resolveZoneKeyForTarget(
  zones: Record<string, ZoneEntry>,
  targetTexture: Pick<StoredTextureLike, 'zoneName' | 'zoneDisplayName' | 'zone'>,
): string | null {
  if (!zones || !targetTexture) return null

  const zoneName = targetTexture.zoneName?.trim()
  if (zoneName && zones[zoneName]) return zoneName

  return findZoneKeyForTexture(zones, targetTexture as StoredTextureLike)
}

export function findTextureForMeshNode(
  nodeName: string,
  zones: Record<string, ZoneEntry>,
  textures: StoredTextureLike[],
  materialName?: string,
): StoredTextureLike | undefined {
  const direct = textures.find(
    (t) => t.zoneName === nodeName || zoneNamesMatch(t.zoneName, nodeName),
  )
  if (direct) return direct

  const zone = zones[nodeName]
  let logical =
    getZoneLogicalName(zone?.material) ||
    getZoneLogicalName(zone) ||
    getZoneLogicalName({ name: zone?.name })
  if (
    !logical &&
    (looksLikeCostadoName(nodeName) || looksLikeCostadoName(materialName))
  ) {
    logical = 'Costado'
  }
  if (!logical) return undefined

  const matches = textures.filter((t) => {
    const textureLogical = getZoneLogicalName(t)
    return (
      t.zoneDisplayName === logical ||
      t.zone === logical ||
      textureLogical === logical ||
      doorLogicalNamesMatch(textureLogical, logical)
    )
  })
  if (matches.length === 0) {
    if (logical === 'Costado') {
      return textures.find(
        (t) => t.zoneDisplayName === 'Puerta' || t.zone === 'Puerta',
      )
    }
    return undefined
  }

  return (
    matches.find((t) => t.zoneName && zones[t.zoneName]) ??
    matches[0]
  )
}

export function textureZoneAlreadyProcessed(
  processedZoneNames: Set<string>,
  texture: Pick<StoredTextureLike, 'zoneName' | 'zoneDisplayName'>,
  newTextures: Array<Pick<StoredTextureLike, 'zoneName'>>,
): boolean {
  if (texture.zoneName && processedZoneNames.has(texture.zoneName)) {
    return true
  }

  return newTextures.some(
    (entry) =>
      Boolean(texture.zoneName) &&
      Boolean(entry.zoneName) &&
      entry.zoneName === texture.zoneName,
  )
}

/**
 * Empareja la textura aplicada por el usuario con una zona del NUEVO GLB usando la
 * zona lógica (`zoneDisplayName`/`zone`) como clave estable.
 *
 * Necesario al cambiar de unidad (p. ej. Gola → Lineal): las claves de mesh
 * (`Mesh001_1`…) se desplazan cuando aparecen/desaparecen zonas (Gola), de modo que
 * emparejar por clave de mesh asigna la textura a la zona equivocada (el Armazón
 * heredaría la visual de la Gola). El nombre lógico de zona no se desplaza.
 */
export function resolveUserTextureByDisplayName<T extends StoredTextureLike>(
  zoneDisplayName: string,
  zone: string,
  textures: T[],
): T | undefined {
  const targets = [zoneDisplayName, zone].filter(Boolean)
  if (targets.length === 0) return undefined

  for (const target of targets) {
    const matches = textures.filter(
      (t) => t.zoneDisplayName === target || t.zone === target,
    )
    if (matches.length === 1) return matches[0]
    if (matches.length > 1) {
      // Varias coincidencias (raro): preferir la que el usuario aplicó realmente.
      const userApplied = matches.find((t) => isUserAppliedLinkedTexture(t))
      if (userApplied) return userApplied
      return matches[0]
    }
  }

  return undefined
}

/** Busca textura de usuario para un mesh del GLB; prioriza zoneName sobre displayName. */
export function resolveUserTextureForGlbZone<T extends StoredTextureLike>(
  zoneName: string,
  zoneDisplayName: string,
  textures: T[],
  primaryByZoneName: Map<string, T>,
  fallbackByZoneName: Map<string, T>,
): T | undefined {
  const byZone =
    primaryByZoneName.get(zoneName) ??
    fallbackByZoneName.get(zoneName) ??
    textures.find((t) => t.zoneName === zoneName)

  if (byZone) return byZone

  const displayMatches = textures.filter(
    (t) => t.zoneDisplayName === zoneDisplayName || t.zone === zoneDisplayName,
  )
  if (displayMatches.length === 1) return displayMatches[0]
  if (displayMatches.length > 1) {
    return displayMatches.find((t) => isUserAppliedLinkedTexture(t)) ?? displayMatches[0]
  }

  return undefined
}

export function collectValidZonesFromUnitAcabados(acabados: unknown): Set<string> {
  const valid = new Set<string>()
  if (!Array.isArray(acabados)) return valid
  acabados.forEach((acabado: { zona?: string; descripcion?: string }) => {
    if (acabado.zona) valid.add(acabado.zona)
    if (acabado.descripcion) valid.add(acabado.descripcion)
  })
  return valid
}

export function isTextureValidForUnitAcabados(
  texture: Pick<StoredTextureLike, 'zoneDisplayName' | 'zone' | 'zoneName'>,
  validZones: Set<string>,
): boolean {
  if (texture.zoneDisplayName && validZones.has(texture.zoneDisplayName)) return true
  if (texture.zone && validZones.has(texture.zone)) return true
  if (texture.zoneName && validZones.has(texture.zoneName)) return true
  if (texture.zoneDisplayName === 'Armazon') return true
  return false
}

function looksLikeCostadoName(name?: string | null): boolean {
  if (!name) return false
  return /^costado$/i.test(String(name).replace(/[0-9]/g, '').trim())
}

function isCostadoLogicalName(name?: string | null): boolean {
  return name === 'Costado'
}

function textureIsCostadoZone(
  texture?: Pick<StoredTextureLike, 'zoneDisplayName' | 'zone'> | null,
): boolean {
  return Boolean(
    texture &&
      (isCostadoLogicalName(texture.zoneDisplayName) || isCostadoLogicalName(texture.zone)),
  )
}

/**
 * Costado solo existe en laterales/columnas (LA, LB, LC, CC, CB).
 * En MAL/Gola el GLB o la herencia de escena etiquetan a veces la puerta como Costado.
 */
export function sanitizeDisallowedCostadoOnLinkedItem(item?: {
  catalogID?: string | number
  codigo?: string | number
  textures?: StoredTextureLike[]
  zones?: Record<string, ZoneEntry>
} | null): boolean {
  if (!item || isLateralOrCostadoLinkedCatalog(item)) return false

  let changed = false
  const textures = item.textures
  if (Array.isArray(textures) && textures.length > 0) {
    const hasPuerta = textures.some(
      (texture) => texture.zoneDisplayName === 'Puerta' || texture.zone === 'Puerta',
    )
    const nextTextures: StoredTextureLike[] = []
    for (const texture of textures) {
      if (!textureIsCostadoZone(texture)) {
        nextTextures.push(texture)
        continue
      }
      changed = true
      if (hasPuerta) continue
      nextTextures.push({
        ...texture,
        zoneDisplayName: 'Puerta',
        zone: 'Puerta',
      })
    }
    if (changed) item.textures = nextTextures
  }

  if (!item.zones) return changed

  for (const zone of Object.values(item.zones)) {
    const mat = zone?.material
    if (!mat) continue
    if (!isCostadoLogicalName(mat.zoneDisplayName) && !isCostadoLogicalName(mat.zone)) continue
    mat.zoneDisplayName = 'Puerta'
    mat.zone = 'Puerta'
    changed = true
  }

  return changed
}
